import type { ReactNode } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { Funnel, PlatformComponentProps } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { HsbcPromotedDestinations } from "@hsbc/portal";
import { PremierDestinationEarnDialog } from "@hsbc/portal";
import { Dialog, DialogTrigger, DialogContent, DialogClose } from "@tenant/ui";
import clsx from "clsx";
import styles from "./styles.module.scss";

interface PremierDestinationCountryTriggerProps extends PlatformComponentProps {
  country: HsbcPromotedDestinations;
}

export const PremierDestinationCountryTrigger = ({
  className,
  country,
  children,
}: PremierDestinationCountryTriggerProps) => {
  const [, cn] = useModuleBEM(styles, "PremierDestinationBanner");
  const hasBackgroundImage = country.image != null;

  return (
    <div
      className={clsx(
        className,
        cn("country", {
          lightText:
            !hasBackgroundImage || country.styles.textColor === "white",
        })
      )}
      style={
        hasBackgroundImage
          ? {
              ["--background-image" as string]: `url(${country.image})`,
            }
          : undefined
      }
    >
      {children}
    </div>
  );
};

export interface PremierDestinationBannerProps extends PlatformComponentProps {
  funnel: Funnel;
  countryCode?: string[];
  title?: ReactNode;
  description?: ReactNode;
  activePremierDestinations: HsbcPromotedDestinations[];
}

export const PremierDestinationBanner = ({
  countryCode,
  className,
  title,
  activePremierDestinations,
}: PremierDestinationBannerProps) => {
  const [block, cn] = useModuleBEM(styles, "PremierDestinationBanner");
  const { t } = useI18nContext();

  const country = activePremierDestinations.find((c) =>
    countryCode ? countryCode.includes(c.countryCode) : false
  );

  if (!country) return null;

  return (
    <Dialog>
      <DialogTrigger className={block}>
        <PremierDestinationCountryTrigger
          country={country}
          className={className}
        >
          {title ??
            t("premierDestinations.bannerLabel", { country: country.title })}
        </PremierDestinationCountryTrigger>
      </DialogTrigger>
      <DialogContent
        title={t("premierDestinations.banner.title", {
          country: country.title,
        })}
        className={cn("dialog-content")}
      >
        <PremierDestinationEarnDialog />

        <DialogClose className={cn("close-button")}>
          {t("close.button")}
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};
